import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, ChevronLeft, ChevronRight, Loader2, Plus, Send } from "lucide-react";
import { api, errMsg, fmtDate, fmtDateTime } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Field, FileUploader, PageTitle, Chip } from "../components/common";
import PrestationEditor from "../components/declare/PrestationEditor";
import ReturnsEditor from "../components/declare/ReturnsEditor";
import { DateField } from "../components/DateFields";
import { Modal } from "../components/shared";

const satPallets = (f) => f.work_date && new Date(`${f.work_date}T12:00:00`).getDay() === 6 &&
  f.prestations.some((p) => p.components.some((c) => ["del_pallets", "del_pallet_stops", "extra_del_pallets"].some((k) => Number(c.qty?.[k]) > 0)));
import { useLang } from "../lib/i18n";
import { SERVICE_TYPES, QTY_FIELDS, emptyComponent } from "../lib/labels";

const newKey = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
const today = () => new Date().toISOString().slice(0, 10);
const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
const initial = () => ({ key: newKey(), work_date: "", driver_id: "", new_driver_name: "", employee_id: "", route_id: "", observations: "",
  prestations: [{ mixed: false, components: [emptyComponent()], observations: "" }], hasReturns: null, returns: [], pm: [], diad: [], piece: [] });

function toPayload(f) {
  return {
    idempotency_key: f.key, work_date: f.work_date, driver_id: f.driver_id === "__new" ? "" : f.driver_id, new_driver_name: f.driver_id === "__new" ? f.new_driver_name : "",
    employee_id: f.employee_id, route_id: f.route_id, observations: f.observations, duplicate_confirmed: !!f.dupConfirmed,
    prestations: f.prestations.map((p) => ({ mixed: p.mixed, observations: p.observations, components: p.components.map((c) => ({
      type: c.type, zone: c.zone, qty: Object.fromEntries(QTY_FIELDS.map(([k]) => [k, num(c.qty[k])])),
      special: ["special", "special_fret", "express_plus"].includes(c.type) ? { destination: c.special.destination, address: c.special.address, km: num(c.special.km),
        kind: c.special.kind || "", nature: c.special.nature || "", origin: c.special.origin || "", start: c.special.start || "", end: c.special.end || "",
        remarks: c.special.remarks || "", photo_ids: (c.special.photos || []).map((x) => x.id),
        minutes: c.special.hours === "" && c.special.minutes === "" ? null : (Number(c.special.hours || 0) * 60 + Number(c.special.minutes || 0)) } : null })) })),
    has_returns: f.hasReturns,
    returns: f.hasReturns ? f.returns.map((r) => ({ motif: r.motif, motif_other: r.motif_other, quantity: Number(r.quantity || 0), rank: num(r.rank),
      parcel_ref: r.parcel_ref, description: r.description, photo_ids: r.photos.map((x) => x.id) })) : [],
    attachments: { pm_report: f.pm.map((x) => x.id), diad: f.diad.map((x) => x.id), piece: f.piece.map((x) => x.id) },
  };
}

function validate(step, f) {
  if (step === 0) {
    if (!f.work_date) return "Choisissez la date réelle de la prestation.";
    if (f.work_date > today()) return "La date ne peut pas être dans le futur.";
    if (!f.driver_id) return "Choisissez le chauffeur.";
    if (f.driver_id === "__new" && !f.new_driver_name.trim()) return "Indiquez le nom du nouveau chauffeur.";
    if (!f.employee_id.trim()) return "L'ID du chauffeur (Employee ID) est obligatoire.";
    if (!f.employee_id.trim() || !f.route_id.trim()) return "Employee ID et Route ID sont obligatoires.";
    if (f.dupOk !== `${f.work_date}|${f.employee_id}|${f.route_id}`) return "__dup";
  }
  if (step === 1 && f.prestations.some((p) => p.mixed && p.components.length < 2)) return "Une prestation mixte doit avoir au moins 2 composantes.";
  if (step === 2) {
    if (f.hasReturns === null) return "Répondez à la question « Avez-vous des retours ? ».";
    for (const r of f.hasReturns ? f.returns : []) {
      if (!Number(r.quantity)) return "Indiquez la quantité de chaque retour.";
      if (["AUTRE", "REROUTE"].includes(r.motif) && !r.motif_other.trim()) return `Précisez le motif « ${r.motif} ».`;
      if (r.motif === "DAMAGE" && r.photos.length < Number(r.quantity)) return "DAMAGE : une photo par colis endommagé est obligatoire.";
    }
  }
  return null;
}

function Recap({ f, drivers, user }) {
  const drv = drivers.find((d) => d.id === f.driver_id);
  return (
    <div className="tl-card space-y-3" data-testid="recap">
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div><span className="tl-label">Date travaillée</span><b data-testid="recap-date">{fmtDate(f.work_date)}</b></div>
        <div><span className="tl-label">Société</span><b>{user.company_name || "Ma société"}</b></div>
        <div><span className="tl-label">Chauffeur</span><b data-testid="recap-driver">{f.driver_id === "__new" ? `${f.new_driver_name} (nouveau)` : drv?.name}</b></div>
        <div><span className="tl-label">Employee ID / Route</span><b className="tl-mono">{f.employee_id} · {f.route_id}</b></div>
      </div>
      {f.prestations.map((p, i) => (
        <div key={i} className="border-t border-slate-200 pt-2">
          <div className="font-semibold">Prestation {i + 1} {p.mixed && <Chip tone="amber">mixte</Chip>}</div>
          {p.components.map((c, j) => (
            <div key={j} className="text-sm mt-1"><b>{SERVICE_TYPES[c.type]}</b> — {QTY_FIELDS.map(([k, l]) => `${l} : ${c.qty[k] === "" ? "non renseigné" : c.qty[k]}`).join(" · ")}
              {["special", "special_fret"].includes(c.type) && ` · ${c.special.destination || "destination ?"}, ${c.special.km || "?"} km, ${c.special.hours || 0}h${c.special.minutes || 0}`}</div>
          ))}
        </div>
      ))}
      <div className="border-t border-slate-200 pt-2 text-sm">Retours : <b>{f.hasReturns ? f.returns.map((r) => `${r.quantity} × ${r.motif === "AUTRE" ? r.motif_other : r.motif}`).join(", ") : "aucun"}</b></div>
      <div className="text-sm">Pièces : {f.pm.length} PM Report · {f.diad.length} photo DIAD · {f.piece.length} autre(s)</div>
    </div>
  );
}

function Done({ result, onAgain }) {
  return (
    <div className="tl-card text-center space-y-4 py-8 tl-fade" data-testid="declaration-success">
      <CheckCircle2 className="mx-auto text-emerald-600" size={56} />
      <h2 className="text-3xl font-bold uppercase">Déclaration reçue</h2>
      <div className="tl-mono text-2xl" data-testid="declaration-reference">{result.ref}</div>
      <div className="flex justify-center gap-2 flex-wrap">
        <Chip tone="green" testid="status-received">Déclaration reçue · {fmtDateTime(result.submitted_at)}</Chip>
        <Chip tone={result.excel_status === "integree" ? "blue" : "slate"} testid="status-excel">{result.excel_status === "integree" ? "Intégrée dans Excel" : "Pas encore intégrée dans Excel"}</Chip>
      </div>
      {result.duplicate_submit && <p className="text-sm text-amber-800">Cet envoi avait déjà été enregistré : aucune nouvelle déclaration créée.</p>}
      <p className="text-sm text-slate-600">Date travaillée : {fmtDate(result.work_date)} · {result.driver_name} · {result.prestations.length} prestation(s)</p>
      <button className="tl-btn tl-btn-primary text-lg" onClick={onAgain} data-testid="declare-another-button"><Plus size={20} /> Déclarer une autre journée</button>
    </div>
  );
}

export default function Declare() {
  const { user } = useAuth();
  const { t } = useLang();
  const steps = t("decl.steps");
  const [f, setF] = useState(initial);
  const [drivers, setDrivers] = useState([]);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [dups, setDups] = useState(null);
  const set = (patch) => setF((x) => ({ ...x, ...patch }));
  const dupKey = `${f.work_date}|${f.employee_id}|${f.route_id}`;

  useEffect(() => {
    api.get("/drivers").then((r) => {
      setDrivers(r.data);
      if (user.role === "chauffeur" && r.data[0]) setF((x) => ({ ...x, driver_id: r.data[0].id, employee_id: x.employee_id || r.data[0].employee_id || "" }));
    });
  }, [user.role]);

  const pickDriver = (id) => {
    const d = drivers.find((x) => x.id === id);
    set({ driver_id: id, employee_id: d?.employee_id || f.employee_id });
  };
  const next = async () => {
    const err = validate(step, f);
    if (err === "__dup") {
      const { data } = await api.get("/duplicates-check", { params: { work_date: f.work_date, employee_id: f.employee_id, route_id: f.route_id } });
      if (data.duplicates.length) return setDups(data.duplicates);
      set({ dupOk: dupKey });
      return setStep(step + 1);
    }
    if (err) return toast.error(err);
    setStep(step + 1);
  };
  const confirmDup = () => { set({ dupOk: dupKey, dupConfirmed: true }); setDups(null); setStep(1); };
  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/declarations", toPayload(f));
      setResult(data);
    } catch (e) {
      toast.error(`${errMsg(e)} Votre saisie est conservée : vous pouvez renvoyer sans créer de doublon.`);
    }
    setBusy(false);
  };
  const again = () => {
    const d = user.role === "chauffeur" ? drivers[0] : null;
    setF({ ...initial(), ...(d ? { driver_id: d.id, employee_id: d.employee_id || "" } : {}) });
    setResult(null); setStep(0); window.scrollTo(0, 0);
  };

  if (result) return <div className="max-w-xl mx-auto"><Done result={result} onAgain={again} /></div>;
  return (
    <div className="max-w-xl mx-auto">
      <PageTitle title={t("decl.title")} sub={t("decl.sub")} />
      <ol className="flex gap-1 mb-5" data-testid="wizard-steps">
        {steps.map((s, i) => (
          <li key={s} className={`flex-1 h-1.5 rounded-full ${i <= step ? "bg-amber-600" : "bg-slate-200"}`} title={s} />
        ))}
      </ol>
      <div className="text-xs uppercase tracking-widest font-bold text-amber-700 mb-3" data-testid="current-step-label">{t("decl.step")} {step + 1} / 5 · {steps[step]}</div>
      {step === 0 && (
        <div className="tl-card space-y-4 tl-fade">
          <Field label={t("decl.date")}><DateField max={today()} value={f.work_date} onChange={(v) => set({ work_date: v })} testid="work-date-input" /></Field>
          {user.role === "chauffeur" ? (
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3" data-testid="driver-self"><span className="tl-label">{t("decl.driver")}</span><b className="text-lg">{drivers[0]?.name || user.name}</b></div>
          ) : (
          <Field label={t("decl.driver")}>
            <select className="tl-select" value={f.driver_id} onChange={(e) => pickDriver(e.target.value)} data-testid="driver-select">
              <option value="">{t("decl.choose")}</option>
              {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              {user.role === "soustraitant" && <option value="__new">{t("decl.newDriver")}</option>}
            </select>
          </Field>
          )}
          {f.driver_id === "__new" && (
            <Field label={t("decl.newDriverName")}><input className="tl-input" value={f.new_driver_name} onChange={(e) => set({ new_driver_name: e.target.value })} data-testid="new-driver-name-input" /></Field>
          )}
          <Field label={t("decl.emp")}><input className="tl-input tl-mono uppercase text-lg py-3" value={f.employee_id} onChange={(e) => set({ employee_id: e.target.value })} data-testid="employee-id-input" /></Field>
          <Field label={t("decl.route")}><input className="tl-input tl-mono text-lg py-3" inputMode="text" value={f.route_id} onChange={(e) => set({ route_id: e.target.value })} data-testid="route-id-input" /></Field>
        </div>
      )}
      {step === 1 && (
        <div className="space-y-4">
          {f.prestations.map((p, i) => (
            <PrestationEditor key={i} p={p} pi={i} onChange={(np) => set({ prestations: f.prestations.map((x, j) => (j === i ? np : x)) })}
              onRemove={f.prestations.length > 1 ? () => set({ prestations: f.prestations.filter((_, j) => j !== i) }) : null} />
          ))}
          <button type="button" className="tl-btn tl-btn-ghost w-full" onClick={() => set({ prestations: [...f.prestations, { mixed: false, components: [emptyComponent()], observations: "" }] })} data-testid="add-prestation-button">
            <Plus size={18} /> Ajouter une prestation séparée
          </button>
        </div>
      )}
      {step === 2 && <ReturnsEditor hasReturns={f.hasReturns} setHasReturns={(v) => set({ hasReturns: v })} returns={f.returns} setReturns={(r) => set({ returns: r })} />}
      {step === 3 && (
        <div className="tl-card space-y-4 tl-fade">
          <FileUploader kind="pm_report" files={f.pm} onChange={(v) => set({ pm: v })} label="PM Report (facultatif)" testid="upload-pm-report" />
          <FileUploader kind="diad" accept="image/*" files={f.diad} onChange={(v) => set({ diad: v })} label="Photo du scan DIAD" testid="upload-diad" />
          <FileUploader kind="piece" files={f.piece} onChange={(v) => set({ piece: v })} label="Pièces complémentaires" testid="upload-piece" />
          <Field label="Observations"><textarea className="tl-textarea" value={f.observations} onChange={(e) => set({ observations: e.target.value })} data-testid="declaration-observations" /></Field>
        </div>
      )}
      {step === 4 && <Recap f={f} drivers={drivers} user={user} />}
      {step >= 1 && satPallets(f) && <div className="mt-3 rounded-lg border border-amber-400 bg-amber-50 p-3 text-sm font-semibold text-amber-900" data-testid="saturday-pallet-warning">Palette le samedi : signalé au gestionnaire pour vérification.</div>}
      {dups && (
        <Modal title="Doublon possible" onClose={() => setDups(null)} testid="duplicate-modal">
          <div className="space-y-3">
            <p className="text-sm">Une déclaration existe déjà le {f.work_date} avec : <b>{dups.join(", ")}</b>.</p>
            <p className="text-sm text-slate-600">Vérifiez l'ID chauffeur et le Route ID. Si c'est correct (par exemple deux tournées différentes), confirmez pour continuer : le gestionnaire sera informé.</p>
            <div className="flex gap-2">
              <button className="tl-btn tl-btn-ghost flex-1" onClick={() => setDups(null)} data-testid="duplicate-fix-button">Corriger</button>
              <button className="tl-btn tl-btn-dark flex-1" onClick={confirmDup} data-testid="duplicate-confirm-button">Je confirme, continuer</button>
            </div>
          </div>
        </Modal>
      )}
      <div className="flex gap-2 mt-5 sticky bottom-20 lg:bottom-4">
        {step > 0 && <button className="tl-btn tl-btn-ghost flex-1" onClick={() => setStep(step - 1)} data-testid="wizard-back-button"><ChevronLeft size={18} /> {t("decl.back")}</button>}
        {step < 4 && <button className="tl-btn tl-btn-dark flex-[2]" onClick={next} data-testid="wizard-next-button">{t("decl.next")} <ChevronRight size={18} /></button>}
        {step === 4 && <button className="tl-btn tl-btn-primary flex-[2] text-lg" onClick={submit} disabled={busy} data-testid="submit-declaration-button">{busy ? <Loader2 className="animate-spin" /> : <Send size={18} />} {t("decl.submit")}</button>}
      </div>
    </div>
  );
}
